import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import LoadingSpinner from '../../components/LoadingSpinner';
import TuroComparisonTable from './TuroComparisonTable';

const Container = styled.div`
  ${tw`min-h-screen bg-bg-secondary pb-8 md:pb-16`}
`;

const ContentWrapper = styled.div`
  ${tw`max-w-7xl mx-auto px-4 py-8`}
`;

const LoadingContainer = styled.div`
  ${tw`flex justify-center items-center min-h-screen`}
`;

const ErrorContainer = styled.div`
  ${tw`flex justify-center items-center min-h-screen`}
`;

const ErrorMessage = styled.div`
  ${tw`text-red-600 text-lg`}
`;

const GuideContent = styled.div`
  ${tw`bg-white rounded-lg shadow-card p-8`}

  h1 {
    ${tw`text-3xl font-bold mb-4 text-text-primary`}
  }

  h2 {
    ${tw`text-2xl font-bold mt-8 mb-4 text-text-primary`}
  }

  h3 {
    ${tw`text-xl font-bold mt-6 mb-3 text-text-secondary`}
  }

  h4 {
    ${tw`text-lg font-bold mt-4 mb-2 text-text-secondary`}
  }

  p {
    ${tw`mb-4 text-text-secondary leading-relaxed`}
  }

  ul,
  ol {
    ${tw`mb-4 pl-6`}
  }

  li {
    ${tw`mb-2 text-text-secondary`}
  }

  hr {
    ${tw`my-8 border-border-default`}
  }

  table {
    ${tw`w-full border-collapse mb-6`}
    border: 1px solid var(--color-border);

    th,
    td {
      ${tw`px-4 py-3 border border-border-default text-left`}
    }

    th {
      ${tw`bg-bg-secondary font-semibold text-text-secondary`}
    }

    td {
      ${tw`text-text-secondary`}
    }
  }

  code {
    ${tw`bg-bg-secondary px-2 py-1 rounded-md text-sm font-mono`}
  }

  pre {
    ${tw`bg-bg-secondary p-4 rounded-md overflow-x-auto mb-4`}

    code {
      ${tw`bg-transparent p-0`}
    }
  }

  blockquote {
    ${tw`border-l-4 border-border-default pl-4 italic text-text-secondary my-4`}
  }

  a {
    ${tw`text-black underline`}

    &:hover {
      ${tw`text-blue-800`}
    }
  }
`;

interface HostGuidePageProps {
  language: 'zh' | 'en';
}

const HostGuidePage: React.FC<HostGuidePageProps> = ({ language }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHtml = async () => {
      try {
        const fileName = language === 'zh' ? 'host-guide-zh.html' : 'host-guide-en.html';
        const response = await fetch(`/${fileName}`);
        if (!response.ok) {
          throw new Error(`Failed to load ${fileName}`);
        }
        const text = await response.text();
        setHtmlContent(text);
      } catch (err) {
        setError(`Unable to load the guide. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };

    fetchHtml();
  }, [language]);

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </ErrorContainer>
    );
  }

  // Extract the HTML content but replace the comparison table section with our TSX component
  // We need to extract the content before and after the comparison table
  const tableStartMarker =
    language === 'zh'
      ? '<h2>Autopilotee vs Turo 功能对比</h2>'
      : '<h2>Autopilotee vs Turo Feature Comparison</h2>';

  const tableStartIndex = htmlContent.indexOf(tableStartMarker);

  let beforeTable = '';
  let afterTable = '';

  if (tableStartIndex !== -1) {
    // Extract content before the table section
    beforeTable = htmlContent.substring(0, tableStartIndex);

    // Find the end of the table section - look for </table> followed by <hr>
    const tableSectionStart = tableStartIndex;
    const tableEndPattern = '</table>';
    const hrPattern = '<hr>';

    // Find the closing </table> tag
    let tableEndIndex = htmlContent.indexOf(tableEndPattern, tableSectionStart);
    if (tableEndIndex === -1) {
      // Fallback: look for <hr> after the table start
      tableEndIndex = htmlContent.indexOf(hrPattern, tableSectionStart);
    } else {
      // Find the <hr> after </table>
      const hrAfterTable = htmlContent.indexOf(hrPattern, tableEndIndex + tableEndPattern.length);
      if (hrAfterTable !== -1) {
        afterTable = htmlContent.substring(hrAfterTable + hrPattern.length);
      } else {
        afterTable = htmlContent.substring(tableEndIndex + tableEndPattern.length);
      }
    }

    if (afterTable === '') {
      // If we couldn't find the end properly, just take everything after </table>
      if (tableEndIndex !== -1) {
        afterTable = htmlContent.substring(tableEndIndex + tableEndPattern.length);
      } else {
        afterTable = htmlContent.substring(tableSectionStart + tableStartMarker.length);
      }
    }
  } else {
    // If we can't find the markers, just use the full content
    beforeTable = htmlContent;
  }

  return (
    <Container>
      <ContentWrapper>
        <GuideContent>
          {beforeTable && <div dangerouslySetInnerHTML={{ __html: beforeTable }} />}
          <TuroComparisonTable language={language} />
          {afterTable && <div dangerouslySetInnerHTML={{ __html: afterTable }} />}
        </GuideContent>
      </ContentWrapper>
    </Container>
  );
};

export default HostGuidePage;
